import {useMemo, useState} from 'react';
import {Moment} from 'moment';
import {Button, Stack, SxProps, Typography} from '@mui/material';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {maxDateTimeAllow} from '../../screens/private/office/professional-caregiver/planner/calendar/shared/date-max-time';
import {DateRangeDropdown} from './date-range-dropdown';
import {
  calcAmountType,
  calcNewDates,
  calcRangeTypeByDates,
  DateRangeMode,
  DateRangeOnChange,
} from './date-range-utils';

export type DateRangeInputProps = {
  startDate: Moment;
  endDate?: Moment;
  mode?: DateRangeMode;
  disabledFuture?: boolean;
  onChange?: DateRangeOnChange;
};

export const DateRangeInput = ({
  startDate,
  endDate = startDate,
  mode = 'default',
  disabledFuture,
  onChange,
}: DateRangeInputProps) => {
  // Disable next button if date is after max time
  const disabledNextClick = useMemo(() => {
    return endDate.isSameOrAfter(maxDateTimeAllow());
  }, [endDate]);

  const handleBackClick = () => {
    // Calculate new dates
    const range = calcRangeTypeByDates(startDate, endDate);
    const dates = calcNewDates(startDate, endDate, 'subtract');

    // Fire changes
    onChange?.(dates.startDate, dates.endDate, range);
  };

  const handleNextClick = () => {
    // Calculate new dates
    const range = calcRangeTypeByDates(startDate, endDate);
    const dates = calcNewDates(startDate, endDate, 'add');

    // Fire changes
    onChange?.(dates.startDate, dates.endDate, range);
  };

  const arrowBtnSx: SxProps = {
    padding: 0,
    minWidth: 0,
    width: '40px',
    height: '40px',
    '& .MuiSvgIcon-root': {
      fontSize: '32px',
    },
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  };

  return (
    <Stack minWidth="fit-content" direction="row" alignItems="center" spacing={0}>
      <Button onClick={handleBackClick} sx={arrowBtnSx}>
        <KeyboardArrowLeft />
      </Button>

      <MiddleButton
        startDate={startDate}
        endDate={endDate}
        mode={mode}
        disabledFuture={disabledFuture}
        onChange={onChange}
      />

      <Button onClick={handleNextClick} sx={arrowBtnSx} disabled={disabledNextClick}>
        <KeyboardArrowRight />
      </Button>
    </Stack>
  );
};

// -------------------------------------------------------------------------------
// -------------------------------------------------------------------------------
// -------------------------------------------------------------------------------

type MiddleButtonProps = {
  startDate: Moment;
  endDate: Moment;
  mode: DateRangeMode;
  disabledFuture?: boolean;
  onChange?: DateRangeOnChange;
};

function MiddleButton({startDate, endDate, disabledFuture, mode, onChange}: MiddleButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const formattedDate = useMemo(() => {
    const currentYear = new Date().getFullYear();
    // Add year suffix if different from current year
    const suffixYear = currentYear !== startDate.year() ? 'YY' : '';
    const amountType = calcAmountType(startDate, endDate);

    // (Special) Day view
    if (amountType === 'day') {
      return startDate.format(`DD MMM ${suffixYear}`);
    }

    // (Special) Month view
    if (amountType === 'month') {
      return startDate.format(`MMM ${suffixYear}`);
    }

    // (Default) Between 2 dates view
    const suffixYearNextWeek = currentYear !== endDate.year() ? 'YY' : '';
    return `${startDate.format(`DD MMM ${suffixYear}`)} - ${endDate.format(`DD MMM ${suffixYearNextWeek}`)}`;
  }, [endDate, startDate]);

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          color: 'white',
          textOverflow: 'ellipsis',
          textWrap: 'nowrap',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Typography
          sx={{fontSize: '22px', color: 'white', textTransform: 'capitalize', textAlign: 'center'}}
        >
          {formattedDate}
        </Typography>
      </Button>

      {!!anchorEl && (
        <DateRangeDropdown
          anchorEl={anchorEl}
          open
          mode={mode}
          startDate={startDate}
          endDate={endDate}
          onClose={handleClose}
          disabledFuture={disabledFuture}
          onConfirm={(startDate, endDate, range) => {
            handleClose();
            onChange?.(startDate, endDate, range);
          }}
        />
      )}
    </>
  );
}
